import { atom, selector, selectorFamily } from 'recoil'
import { UserList, UserListItem } from 'src/common-services'
import { keyFactory } from 'src/utils/recoil'

const key = keyFactory('user-lists')

export const userListItem = atom<UserListItem | null>({
  key: key('user-list-item'),
  default: null,
})

export const userLists = atom<UserList[] | undefined>({
  key: key('user-lists'),
  // don't load this on the server
  default: undefined,
})

export const userListItemDetails = selector<UserListItem['data'] | undefined>({
  key: key('user-list-item-details'),
  get: ({ get }) => {
    const item = get(userListItem)
    const itemSavedIn = get(savedIn(item))
    if (!item) {
      return
    }
    if (itemSavedIn.length === 0) {
      return item.data
    }
    return itemSavedIn[0].items.find((it) => {
      if ('googlePlaceId' in it.data && 'googlePlaceId' in item.data) {
        return it.data.googlePlaceId === item.data.googlePlaceId
      }
      if ('locationId' in it.data && 'locationId' in item.data) {
        return it.data.locationId === item.data.locationId
      }
      // TODO: Check for custom area and parcel
    })?.data
  },
})

export const savedIn = selectorFamily<UserList[], UserListItem | null | undefined>({
  key: key('saved-in'),
  get:
    (item?: UserListItem | null | undefined) =>
    ({ get }) => {
      if (!item) {
        return []
      }
      const lists = get(userLists)
      if (!lists) {
        return []
      }
      return lists.filter((list) => {
        return list.items?.some((listItem) => {
          // Addresses
          if ('googlePlaceId' in listItem.data && 'googlePlaceId' in item.data) {
            return listItem.data.googlePlaceId === item.data.googlePlaceId
          }
          // Locations / Shopping Centers
          if ('locationId' in listItem.data && 'locationId' in item.data) {
            return listItem.data.locationId === item.data.locationId
          }
          // Custom Areas
          if (listItem.type === 'custom-area') {
            if ('center' in listItem.data && 'center' in item.data) {
              return JSON.stringify(listItem.data.center) === JSON.stringify(item.data.center)
            }
            if ('coordinates' in listItem.data && 'coordinates' in item.data) {
              return JSON.stringify(listItem.data.coordinates) === JSON.stringify(item.data.coordinates)
            }
          }
          // Parcels
          if (listItem.type === 'parcel') {
            if ('geometry' in listItem.data && 'geometry' in item.data) {
              return JSON.stringify(listItem.data.geometry) === JSON.stringify(item.data.geometry)
            }
          }
        })
      })
    },
})

export const isSaved = selectorFamily<boolean, UserListItem | null | undefined>({
  key: key('is-saved'),
  get:
    (item?: UserListItem | null | undefined) =>
    ({ get }) => {
      return get(savedIn(item)).length > 0
    },
})
